import { getStatusConfig } from '@/config/status.config';
import { 
  QuoteStatus, 
  TaskStatus, 
  SaleStatus, 
  InvoiceStatus, 
  ShipmentStatus,
  StatusEntityType 
} from '@/services/api/types';

// Type-safe status type mapping
type StatusMap = {
  quotes: QuoteStatus;
  tasks: TaskStatus;
  sales: SaleStatus;
  invoices: InvoiceStatus;
  shipments: ShipmentStatus;
};

/**
 * Get human-readable status label for any entity type
 */
export function getStatusLabel<T extends StatusEntityType>(
  entityType: T,
  status: StatusMap[T]
): string {
  return getStatusConfig(entityType, status).label;
}

/**
 * Get status color for UI for any entity type
 */
export function getStatusColor<T extends StatusEntityType>(
  entityType: T,
  status: StatusMap[T]
): string {
  return getStatusConfig(entityType, status).color;
}

/**
 * Get status badge variant for any entity type
 * This provides a consistent way to determine badge styling
 */
export function getStatusBadgeVariant<T extends StatusEntityType>(
  entityType: T,
  status: StatusMap[T]
): 'default' | 'success' | 'warning' | 'destructive' | 'secondary' {
  const config = getStatusConfig(entityType, status);
  const color = config.color;
  
  // Map colors to badge variants based on our design system
  if (color === '#10B981' || color.includes('success')) return 'success';
  if (color === '#EF4444' || color.includes('error')) return 'destructive';
  if (color === '#FBBF24' || color.includes('warning')) return 'warning';
  if (color === '#6B7280' || color.includes('gray')) return 'secondary';
  
  return 'default';
}

// Export specific helpers for backwards compatibility
export const getQuoteStatusLabel = (status: QuoteStatus) => getStatusLabel('quotes', status);
export const getQuoteStatusColor = (status: QuoteStatus) => getStatusColor('quotes', status);

export const getTaskStatusLabel = (status: TaskStatus) => getStatusLabel('tasks', status);
export const getTaskStatusColor = (status: TaskStatus) => getStatusColor('tasks', status);

export const getSaleStatusLabel = (status: SaleStatus) => getStatusLabel('sales', status);
export const getSaleStatusColor = (status: SaleStatus) => getStatusColor('sales', status);

export const getInvoiceStatusLabel = (status: InvoiceStatus) => getStatusLabel('invoices', status);
export const getInvoiceStatusColor = (status: InvoiceStatus) => getStatusColor('invoices', status);

export const getShipmentStatusLabel = (status: ShipmentStatus) => getStatusLabel('shipments', status);
export const getShipmentStatusColor = (status: ShipmentStatus) => getStatusColor('shipments', status); 